import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";
const primaryCls =
  "w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50";

export const OtpLogin = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [channel, setChannel] = useState("sms");

  useEffect(() => {
    api.get("/auth/otp/channel").then((r) => setChannel(r.data.channel)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/login/send", { phone });
      setSent(data);
      setCooldown(data.resend_in);
      setCode("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/login/verify", { phone, code });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={sent ? verify : send} className="space-y-5" data-testid="otp-login-form">
      {error && <p data-testid="otp-login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>}
      {channel === "email" && !sent && (
        <p className="text-xs text-white/40" data-testid="otp-login-email-hint">Enter your registered mobile number — we'll email the sign-in code to the address on your account.</p>
      )}
      <div className="relative">
        <Smartphone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
        <input data-testid="otp-login-phone-input" type="tel" inputMode="numeric" required placeholder="Registered mobile number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))} disabled={!!sent} className={`${inputCls} pl-10 disabled:opacity-60`} />
      </div>
      {sent ? (
        <>
          <p className="text-xs text-white/50" data-testid="otp-login-sent-message">{sent.message}. Valid for {Math.round(sent.expires_in / 60)} minutes.{sent.channel === "email" && " Check your spam folder too."}</p>
          <input data-testid="otp-login-code-input" inputMode="numeric" maxLength={6} required placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} font-mono tracking-[0.5em]`} autoFocus />
          <button data-testid="otp-login-verify-button" disabled={busy || code.length !== 6} className={primaryCls}>{busy ? "Signing in…" : "Verify & Sign In"}</button>
          <div className="flex justify-between text-xs">
            <button type="button" data-testid="otp-login-edit-phone" onClick={() => { setSent(null); setError(""); }} className="text-white/40 hover:text-[#D4AF37] transition-colors">Change number</button>
            <button type="button" data-testid="otp-login-resend" onClick={send} disabled={busy || cooldown > 0} className="text-white/40 hover:text-[#D4AF37] transition-colors disabled:opacity-40">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
          </div>
        </>
      ) : (
        <button data-testid="otp-login-send-button" disabled={busy || phone.replace(/\D/g, "").length < 10} className={primaryCls}>{busy ? "Sending…" : channel === "email" ? "Email me a code" : "Send OTP"}</button>
      )}
    </form>
  );
};
